import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { DashboardLayout } from "../../../components/organisms/Layout";
import CustomStepper from "../../../components/atoms/Stepper/CustomStepper";
import UserInformation from "./steps/UserInformation";
import CompanyInformation from "./steps/CompanyInformation";
import LocationInformation from "./steps/LocationInformation";
import PreviewRegistration from "./steps/PreviewRegistration";
import { toast } from "react-hot-toast";
import Auth from "../../../api/auth";
import { resetRegistration } from "../../../store/registrationSlice";

const BusinessRegistration = () => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [registrationResponse, setRegistrationResponse] = useState(null);

  useEffect(() => {
    document.title = "HeyHire Portal - Business Registration";
  }, []);

  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (registrationData) => {
    // Ensure we're receiving the correct payload and not an event object
    if (registrationData && !registrationData.nativeEvent) {
      try {
        setLoading(true);
        const response = await Auth.AdminRegister(registrationData);

        if (response.data) {
          await Auth.addAccount(response.data);
          toast.success("Registration submitted successfully");
          setRegistrationResponse(response.data);
          setShowPreview(true);
        }
      } catch (error) {
        console.error("Registration Error:", error);
        const errorMessage =
          error.response?.data?.message || "Error during registration";
        toast.error(errorMessage);

        // Handle validation errors if any
        if (error.response?.data?.errors) {
          Object.entries(error.response.data.errors).forEach(
            ([field, messages]) => {
              console.error(`${field}: ${messages.join(", ")}`);
            }
          );
        }
      } finally {
        setLoading(false);
      }
    } else {
      console.error("Invalid payload received:", registrationData);
      toast.error("Error processing registration data");
    }
  };

  const handleClose = () => {
    // Reset all form data
    dispatch(resetRegistration());
    // Reset to first step
    setCurrentStep(1);
    // Hide preview
    setShowPreview(false);
    // Show success message
    toast.success("Registration completed successfully");
  };

  const steps = ["User Information", "Company Details", "Location"];

  const renderStep = () => {
    if (showPreview) {
      return (
        <PreviewRegistration
          onClose={handleClose}
          registrationResponse={registrationResponse}
        />
      );
    }

    switch (currentStep) {
      case 1:
        return <UserInformation nextStep={nextStep} />;
      case 2:
        return <CompanyInformation nextStep={nextStep} prevStep={prevStep} />;
      case 3:
        return (
          <LocationInformation
            prevStep={prevStep}
            handleSubmit={handleSubmit}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout >
      <div className="max-w-4xl mx-auto px-4 py-4 border border-gray-300 rounded-lg">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Business Registration
        </h1>
        <p className="text-sm text-gray-600 mb-8">
          Complete the registration process by filling out all required
          information
        </p>

        {!showPreview && (
          <CustomStepper steps={steps} currentStep={currentStep} />
        )}

        <div className="bg-white rounded-lg">
          {renderStep()}

          {!showPreview && (
            <div className="px-6 py-4 mt-2 border-t border-gray-200">
              <div className="flex justify-start">
                <span className="text-sm text-gray-500">
                  Step {currentStep} of {steps.length}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BusinessRegistration;
